// src/pages/admin/CreditsPage.tsx
// Give a user platform credits directly (a welcome credit, a goodwill refund),
// without Paystack or a coupon. The gateway records each grant with its
// reason and who gave it, and tells the user in the app and by email.
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Gift, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import { apiClient, creditGrantsApi, getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Notice } from '@/components/ui/Notice';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { Table, THead, TBody, Tr, Th, Td, TableSkeleton } from '@/components/ui/Table';
import { fieldControl, fieldHint, fieldLabel } from '@/components/ui/fieldStyles';

interface UserRow {
  user_id: string;
  email: string;
  full_name: string | null;
  is_active: boolean | number;
}

const MAX_CREDITS = 10_000_000;
const fmt = (n: number) => n.toLocaleString('en-NG');
const when = (s: number) => new Date(s * 1000).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

const CreditsPage: React.FC = () => {
  const qc = useQueryClient();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [user, setUser] = useState<UserRow | null>(null);
  const [credits, setCredits] = useState('');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);

  const users = useQuery<{ users: UserRow[] }>({
    queryKey: ['credit-users', search],
    queryFn: () => apiClient.get('/support/users', { params: { q: search, limit: 10 } }).then(r => r.data),
    enabled: search.trim().length >= 2,
  });

  const grants = useQuery({
    queryKey: ['credit-grants'],
    queryFn: () => creditGrantsApi.list().then(r => r.data.grants),
  });

  const amount = Number(credits);
  const amountOk = Number.isInteger(amount) && amount >= 1 && amount <= MAX_CREDITS;
  const reasonOk = reason.trim().length >= 3;

  const grant = useMutation({
    mutationFn: () => creditGrantsApi.grant(user!.user_id, amount, reason.trim()).then(r => r.data),
    onSuccess: g => {
      setConfirming(false);
      toast.success(`${fmt(g.credits)} credits added to ${g.email ?? 'the account'}. Balance now ${fmt(g.balance ?? g.credits)}. They have been emailed.`);
      setCredits('');
      setReason('');
      setUser(null);
      void qc.invalidateQueries({ queryKey: ['credit-grants'] });
    },
    onError: e => { setConfirming(false); toast.error(getErrorMessage(e)); },
  });

  return (
    <div className="mx-auto max-w-4xl space-y-6 pb-24">
      <PageHeader
        title="Credits"
        description="Add credits to a user's balance directly, for example a welcome credit or a goodwill refund. No payment is taken. The user is told in the app and by email, with your reason. One credit is ₦0.10."
      />

      <Panel title="Give credits">
        <div className="space-y-5">
          <div>
            <label htmlFor="user-search" className={fieldLabel}>User</label>
            {user ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-background/40 px-3.5 py-2.5">
                <div className="min-w-0 text-sm">
                  <div className="truncate text-foreground">{user.email}</div>
                  {user.full_name && <div className="truncate text-muted-foreground">{user.full_name}</div>}
                </div>
                <Button size="sm" variant="outline" onClick={() => setUser(null)}>Change</Button>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <input id="user-search" className={fieldControl} placeholder="Search by email or name"
                    value={query} onChange={e => setQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && setSearch(query.trim())} />
                  <Button variant="outline" className="h-11 shrink-0" leftIcon={<Search className="h-4 w-4" />} onClick={() => setSearch(query.trim())} disabled={query.trim().length < 2}>
                    Search
                  </Button>
                </div>
                {users.isFetching && (
                  <div className="mt-2">
                    <p className="sr-only">Searching…</p>
                    <SkeletonLines count={2} />
                  </div>
                )}
                {users.isError && <Notice tone="error" className="mt-2">{getErrorMessage(users.error)}</Notice>}
                {users.data && !users.isFetching && (
                  users.data.users.length === 0
                    ? <p className="mt-2 text-sm text-muted-foreground">No user matches "{search}".</p>
                    : (
                      <ul className="mt-2 divide-y divide-border overflow-hidden rounded-xl border border-border">
                        {users.data.users.map(u => (
                          <li key={u.user_id}>
                            <button type="button" className="flex w-full flex-wrap items-center gap-x-2 gap-y-0.5 px-3.5 py-2.5 text-left text-sm transition-colors hover:bg-surface-raised"
                              onClick={() => setUser(u)}>
                              <span className="text-foreground">{u.email}</span>
                              {u.full_name && <span className="text-muted-foreground">{u.full_name}</span>}
                              {!u.is_active && <Badge>inactive</Badge>}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )
                )}
              </>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-[12rem_minmax(0,1fr)]">
            <div>
              <label htmlFor="grant-credits" className={fieldLabel}>Credits</label>
              <input id="grant-credits" inputMode="numeric" className={cn(fieldControl, 'font-mono tabular-nums')} placeholder="e.g. 50000"
                value={credits} onChange={e => setCredits(e.target.value.replace(/[^\d]/g, ''))} />
              <p className={cn(fieldHint, 'font-mono tabular-nums')}>
                {credits && amountOk ? `₦${fmt(amount / 10)}` : `1 to ${fmt(MAX_CREDITS)}`}
              </p>
            </div>
            <div>
              <label htmlFor="grant-reason" className={fieldLabel}>Reason</label>
              <input id="grant-reason" className={fieldControl} maxLength={200} placeholder="e.g. Welcome credit"
                value={reason} onChange={e => setReason(e.target.value)} />
              <p className={fieldHint}>The user sees this in their email and notifications.</p>
            </div>
          </div>

          <div className="flex justify-end border-t border-border pt-4">
            <Button onClick={() => setConfirming(true)} disabled={!user || !amountOk || !reasonOk}>Add credits</Button>
          </div>
        </div>
      </Panel>

      <Panel title="Recent grants" bodyClassName="px-0 pb-2">
        {grants.isLoading && <TableSkeleton rows={3} />}
        {grants.isError && <div className="px-5 pb-3"><Notice tone="error">Could not load grants. Try again shortly.</Notice></div>}
        {grants.data && grants.data.length === 0 && (
          <EmptyState icon={<Gift />} title="No credits have been given yet." description="Grants you make appear here, with who gave them and why." />
        )}
        {grants.data && grants.data.length > 0 && (
          <Table minWidth={640}>
            <THead>
              <tr><Th>When</Th><Th>User</Th><Th align="right">Credits</Th><Th>Reason</Th><Th>By</Th></tr>
            </THead>
            <TBody>
              {grants.data.map(g => (
                <Tr key={g.id}>
                  <Td muted className="whitespace-nowrap">{when(g.created_at)}</Td>
                  <Td>{g.email ?? g.user_id}</Td>
                  <Td align="right" mono>{fmt(g.credits)}</Td>
                  <Td muted>{g.reason}</Td>
                  <Td muted>{g.granted_by_email ?? 'Unknown'}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Panel>

      <Modal isOpen={confirming} onClose={() => setConfirming(false)} title="Add credits?" size="sm">
        <p className="text-sm text-muted-foreground">
          Add <strong className="text-foreground">{fmt(amount || 0)} credits</strong> (₦{fmt((amount || 0) / 10)}) to{' '}
          <strong className="text-foreground">{user?.email}</strong> for "{reason.trim()}"? They will be emailed. This can't be undone from here.
        </p>
        <div className="mt-5 flex justify-end gap-3">
          <Button variant="outline" onClick={() => setConfirming(false)}>Cancel</Button>
          <Button onClick={() => grant.mutate()} loading={grant.isLoading}>Add credits</Button>
        </div>
      </Modal>
    </div>
  );
};

export default CreditsPage;
